import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Combobox,
  ScrollArea,
  TextInput,
  useVirtualizedCombobox,
} from '@mantine/core';
import { useId, useUncontrolled } from '@mantine/hooks';
import { useVirtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import deepEqual from 'deep-equal';

import { ClearButton } from '../clearbutton/ClearButton';
import { FormLabel } from '../formlabel/FormLabel';
import { LoadingIndicator } from '../loading-indicator/LoadingIndicator';

import { OPTION_HEIGHT, OPTION_LIST_PADDING } from './helpers/constants';
import { getCurrentItem, hasExactMatch } from './helpers/selectHelpers';
import { useDynamicWidth } from './helpers/useDynamicWidth';
import { FormError, SelectIcon } from './Form';
import { SelectOption } from './SelectOption';
import { SingleSelectProps } from './SingleSelect';

import inputStyles from '../input/input.module.scss';
import dropdownStyles from './dropdown.module.scss';

type CreatableOption<Data> = {
  label: string;
  value: Data;
  isCreateOption?: boolean;
};

export type CreatableSingleSelectProps<Data = unknown> = Omit<
  SingleSelectProps<Data>,
  'searchable'
>;

export function CreatableSingleSelect<Data>({
  className,
  clearable = true,
  data,
  defaultValue,
  description,
  disabled = false,
  dropdownHeight = '40vh',
  dynamicWidthSettings,
  error,
  inlineError = false,
  label,
  loading = false,
  onChange,
  placeholder,
  readOnly = false,
  required,
  role = 'select',
  style,
  tableLayout,
  title,
  value,
  variant = 'regular',
  withinPortal = true,
}: CreatableSingleSelectProps<Data>) {
  const id = useId();
  const [options, setOptions] = useState(data);

  const dataRef = useRef(data);
  useEffect(() => {
    if (!deepEqual(dataRef.current, data, { strict: true })) {
      dataRef.current = data;
      setOptions(data);
    }
  }, [data]);

  const [selection, setSelection] = useUncontrolled({
    value,
    defaultValue,
    onChange,
  });
  const currentItem = getCurrentItem(options, String(selection));
  const [shouldFilterOptions, setShouldFilterOptions] = useState(false);
  const [search, setSearch] = useState(
    currentItem ? currentItem.label || String(currentItem.value) : ''
  );
  const [opened, setOpened] = useState(false);
  const [scrollParent, setScrollParent] = useState<HTMLDivElement | null>(null);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(-1);

  const { t } = useTranslation();

  const filteredOptions = useMemo(() => {
    const trimmedSearch = String(search).trim();
    const baseOptions =
      shouldFilterOptions && trimmedSearch
        ? options.filter((item) =>
            (item.label || String(item.value))
              .toLowerCase()
              .includes(trimmedSearch.toLowerCase())
          )
        : options;

    if (
      !shouldFilterOptions ||
      !trimmedSearch ||
      hasExactMatch(options, trimmedSearch)
    ) {
      return baseOptions;
    }

    const createLabel = t('app.select.createOption', { value: trimmedSearch });
    const createOption: CreatableOption<Data> = {
      label: createLabel,
      value: trimmedSearch as Data,
      isCreateOption: true,
    };

    return [...baseOptions, createOption];
  }, [options, search, shouldFilterOptions, t]);

  const activeOptionIndex = useMemo(
    () =>
      selection != null
        ? filteredOptions.findIndex((item) => item.value === selection)
        : -1,
    [filteredOptions, selection]
  );

  const virtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => scrollParent,
    estimateSize: () => OPTION_HEIGHT,
    overscan: 5,
  });

  function onOptionSubmit(index: number) {
    const newItem = filteredOptions[index] as CreatableOption<Data> | undefined;
    if (!newItem) {
      return;
    }

    if (newItem.isCreateOption) {
      const trimmed = String(search).trim();
      const createdOption = { label: trimmed, value: trimmed as Data };
      setOptions((prev) => [...prev, createdOption]);
      setSelection(createdOption.value);
      setSearch(trimmed);
    } else if (newItem.value !== currentItem?.value) {
      setSelection(newItem.value);
      setSearch(newItem.label || String(newItem.value));
    }

    combobox.closeDropdown();
    combobox.targetRef.current?.blur();
  }

  const combobox = useVirtualizedCombobox({
    opened,
    onOpenedChange: (isOpen) => {
      setOpened(isOpen);
    },
    onDropdownClose: () => {
      setShouldFilterOptions(false);
    },
    onDropdownOpen: () => {
      const openItem = getCurrentItem(options, String(selection));
      if (openItem) {
        setSearch(openItem.label ?? String(openItem.value));
      } else {
        setSearch('');
      }
      if (activeOptionIndex !== -1) {
        virtualizer.scrollToIndex(activeOptionIndex, { align: 'auto' });
      } else {
        virtualizer.scrollToIndex(0, { align: 'end' });
      }
    },
    totalOptionsCount: filteredOptions.length,
    getOptionId: (index) => `${id}-option-${index}`,
    selectedOptionIndex,
    activeOptionIndex,
    setSelectedOptionIndex: (index) => {
      setSelectedOptionIndex(index);
      if (index !== -1) {
        virtualizer.scrollToIndex(index, { align: 'auto' });
      }
    },
    onSelectedOptionSubmit: onOptionSubmit,
  });

  const width = useDynamicWidth(
    dynamicWidthSettings,
    filteredOptions.map((item) => item.label || String(item.value))
  );

  let currentInputValue = '';
  if (opened) {
    currentInputValue = String(search);
  } else if (selection != null && currentItem) {
    currentInputValue = currentItem?.label ?? String(currentItem?.value);
  }

  const clearButton = (
    <ClearButton
      disabled={disabled || readOnly}
      loading={loading}
      onClear={() => {
        if (selection != null) {
          setSelection(null);
        }
      }}
    />
  );

  const selectIcon = (
    <SelectIcon
      disabled={loading || disabled || readOnly}
      error={!!error}
      variant={variant === 'ghost' ? 'primary' : undefined}
    />
  );

  let largeRightSection = false;
  let rightSection: ReactNode | undefined = selectIcon;
  if (loading) {
    rightSection = <LoadingIndicator data-testid="loading-indicator" />;
  } else if (selection != null && clearable && !disabled) {
    largeRightSection = true;
    rightSection = (
      <>
        {clearButton}
        {selectIcon}
      </>
    );
  }

  const sharedInputProps = {
    'data-testid': 'creatable-single-select',
    classNames: {
      error: inputStyles['error'],
      input: clsx(
        inputStyles['input'],
        variant === 'ghost' && inputStyles['input--primary'],
        error && inputStyles['input--error'],
        loading && inputStyles['input--is-loading']
      ),
      section: inputStyles['section'],
      wrapper: clsx(
        inputStyles['wrapper'],
        largeRightSection && inputStyles['wrapper--large-right-section']
      ),
    },
    disabled: loading || disabled,
    readOnly: readOnly,
    error: error && (
      <FormError
        error={typeof error === 'string' ? error : undefined}
        inline={inlineError}
      />
    ),
    id,
    onClick: () => combobox.openDropdown(),
    onFocus: () => combobox.openDropdown(),
    onBlur: () => combobox.closeDropdown(),
    placeholder,
    rightSection,
    unstyled: true,
  };

  return (
    <div className={className} title={title} role={role}>
      <Combobox
        width={width}
        position="bottom-start"
        store={combobox}
        disabled={disabled || loading || readOnly}
        readOnly={readOnly}
        withinPortal={withinPortal}
        resetSelectionOnOptionHover={false}
        classNames={{
          dropdown: dropdownStyles['dropdown'],
        }}
      >
        <div
          className={clsx({ [inputStyles['root--table-layout']]: tableLayout })}
        >
          {label && (
            <FormLabel
              description={description}
              disabled={disabled}
              error={error}
              id={id}
              inline={inlineError}
              label={label}
              badgeIconName={readOnly ? undefined : 'queue'}
              badgeTooltip={
                readOnly ? undefined : t('app.select.creatableHint')
              }
              loading={loading}
              required={required}
            />
          )}
          <Combobox.Target>
            <TextInput
              {...sharedInputProps}
              rightSectionPointerEvents="none"
              value={currentInputValue}
              onChange={(event) => {
                combobox.openDropdown();
                setShouldFilterOptions(true);
                setSearch(event.currentTarget.value);
                setSelectedOptionIndex(-1);
              }}
            />
          </Combobox.Target>
        </div>

        <Combobox.Dropdown>
          <Combobox.Options style={{ width: style?.width }}>
            {filteredOptions.length > 0 ? (
              <ScrollArea.Autosize
                mah={dropdownHeight}
                type="scroll"
                scrollbarSize={4}
                viewportRef={setScrollParent}
                onMouseDown={(event) => event.preventDefault()}
              >
                <div
                  style={{
                    height:
                      virtualizer.getTotalSize() + OPTION_LIST_PADDING * 2,
                    position: 'relative',
                  }}
                >
                  {virtualizer.getVirtualItems().map((virtualItem) => {
                    const item = filteredOptions[
                      virtualItem.index
                    ] as CreatableOption<Data>;
                    const optionKey = item.isCreateOption
                      ? `__create__${String(item.value)}`
                      : String(item.value);
                    return (
                      <SelectOption
                        key={optionKey}
                        value={String(item.value)}
                        active={virtualItem.index === activeOptionIndex}
                        selected={virtualItem.index === selectedOptionIndex}
                        onClick={() => onOptionSubmit(virtualItem.index)}
                        variant={variant === 'ghost' ? 'primary' : undefined}
                        label={item.label || String(item.value)}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: virtualItem.size,
                          transform: `translateY(${virtualItem.start + OPTION_LIST_PADDING}px)`,
                        }}
                      />
                    );
                  })}
                </div>
              </ScrollArea.Autosize>
            ) : (
              <Combobox.Empty>
                {search !== ''
                  ? t('app.select.noOptionsFound')
                  : t('app.select.noOptionsAvailable')}
              </Combobox.Empty>
            )}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </div>
  );
}
